import theme from '@globals/themes/defaultTheme'
import { desktopQuery } from '@globals/themes/themeConstants'
import { useCurrentMember } from '@globals/useCurrentMember'
import useMemberProfileRoute from '@globals/useMemberProfileRoute'
import useTenantMember from '@globals/useTenantMember'
import Tooltip from '@mui/material/Tooltip'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import MemberProfilePreviewCard, {
  MemberProfilePreviewCardSkeleton,
} from './MemberProfilePreviewCard'
import {
  isMemberProfilePreviewEnvironmentActive,
  isPreviewableTenantMember,
} from './memberProfilePreviewUtils'

const LEAVE_DELAY_MS = 120

const tooltipSlotSx: SxProps = {
  p: 0,
  bgcolor: 'transparent',
  maxWidth: 'none',
  boxShadow: 'none',
  color: 'text.primary',
}

const popperSlotSx: SxProps = {
  zIndex: theme.zIndex.drawer,
}

type MemberProfilePreviewWrapperProps = {
  tenantMemberId: string | null | undefined
  children: ReactElement
}

const MemberProfilePreviewWrapper: React.FC<MemberProfilePreviewWrapperProps> = ({
  tenantMemberId,
  children,
}) => {
  const { pathname } = useLocation()
  const isDesktop = useMediaQuery(desktopQuery)
  const getMemberProfileRoute = useMemberProfileRoute()
  const navigate = useNavigate()
  const { member: currentMember } = useCurrentMember()

  const [open, setOpen] = useState(false)
  const [isMemberDeleted, setIsMemberDeleted] = useState(false)

  const isSelf = tenantMemberId === currentMember?.id
  const isEnabled =
    Boolean(tenantMemberId) &&
    !isSelf &&
    !isMemberDeleted &&
    isMemberProfilePreviewEnvironmentActive(pathname, isDesktop, Boolean(currentMember))

  const [member, isFetched, isMemberNotFound] = useTenantMember(
    tenantMemberId ?? '',
    ['avatar', 'publicStaffProfile', 'events', 'email'],
    isEnabled && open,
    // Skip the global 404 interceptor so a deleted member closes the preview instead of routing
    // the whole page to Not Found — same as mention hover (`useHtmlContentWithMentions`).
    true,
  )

  const closePreview = useCallback(() => {
    setOpen(false)
  }, [])

  const handleNavigate = useCallback(() => {
    closePreview()
    if (tenantMemberId) {
      navigate(getMemberProfileRoute(tenantMemberId))
    }
  }, [closePreview, navigate, getMemberProfileRoute, tenantMemberId])

  useEffect(() => {
    setIsMemberDeleted(false)
  }, [tenantMemberId])

  useEffect(() => {
    if (!isEnabled || !open || !isFetched) return
    // 404 or a resolved but unpreviewable member (deleted / suspended): stop fetching on later
    // hovers.
    const memberIsGone = isMemberNotFound || Boolean(member && !isPreviewableTenantMember(member))
    if (memberIsGone) {
      setIsMemberDeleted(true)
      closePreview()
      return
    }
    if (!member) closePreview()
  }, [isEnabled, open, isFetched, isMemberNotFound, member, closePreview])

  useEffect(() => {
    if (!isEnabled) {
      closePreview()
    }
  }, [isEnabled, closePreview])

  if (!isEnabled) {
    return children
  }

  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      leaveDelay={LEAVE_DELAY_MS}
      disableTouchListener
      placement="bottom-start"
      slotProps={{
        tooltip: { sx: tooltipSlotSx },
        popper: { sx: popperSlotSx },
      }}
      title={
        member ? (
          <MemberProfilePreviewCard
            member={member}
            onClose={closePreview}
            onNavigate={handleNavigate}
          />
        ) : (
          <MemberProfilePreviewCardSkeleton />
        )
      }>
      {children}
    </Tooltip>
  )
}

export default MemberProfilePreviewWrapper
